import { useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useCompanySelector } from "@/hooks/useCompanySelector";
import { useConversations, useConversationMessages, useSendMessage, useMarkAsRead, useCreateConversation } from "@/hooks/useTeamComms";
import { usePresence } from "@/hooks/usePresence";
import { useProjectTeam } from "@/hooks/useProjectTeam";
import { MessageThread } from "@/components/controller/comms/MessageThread";
import { ProjectTasklist } from "./ProjectTasklist";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { MessageSquare, ListTodo } from "lucide-react";

interface ProjectCommsTabProps {
  projectId: string;
}

export const ProjectCommsTab = ({ projectId }: ProjectCommsTabProps) => {
  const { user } = useAuth();
  const { activeCompanyId } = useCompanySelector();
  const { data: conversations } = useConversations();
  const { members } = useProjectTeam(projectId);
  const { isOnline } = usePresence(activeCompanyId);
  const sendMessage = useSendMessage();
  const markAsRead = useMarkAsRead();
  const createConversation = useCreateConversation();

  // Find or create project-linked conversation
  const projectConvo = (conversations ?? []).find(c => c.project_id === projectId && c.type === "project");
  const { data: messages, isLoading: msgsLoading } = useConversationMessages(projectConvo?.id ?? null);

  const handleSend = async (body: string) => {
    if (!activeCompanyId) return;

    let convoId = projectConvo?.id;
    if (!convoId) {
      // Auto-create a project channel with all team members
      const result = await createConversation.mutateAsync({
        companyId: activeCompanyId,
        title: `Project Chat`,
        type: "project",
        memberIds: members.map(m => m.user_id),
        projectId,
      });
      convoId = result.id;
    }

    sendMessage.mutate({
      conversationId: convoId!,
      companyId: activeCompanyId,
      body,
      projectId,
    });
  };

  const handleMarkRead = useCallback((messageIds: string[]) => {
    markAsRead.mutate({ messageIds });
  }, [markAsRead]);

  return (
    <Tabs defaultValue="chat" className="space-y-3">
      <TabsList>
        <TabsTrigger value="chat" className="gap-1.5">
          <MessageSquare className="h-4 w-4" /> Chat
        </TabsTrigger>
        <TabsTrigger value="tasks" className="gap-1.5">
          <ListTodo className="h-4 w-4" /> Task List
        </TabsTrigger>
      </TabsList>

      <TabsContent value="chat">
        <div className="h-[500px] rounded-xl border bg-card overflow-hidden flex flex-col">
          {projectConvo || messages ? (
            <MessageThread
              messages={messages ?? []}
              currentUserId={user?.id ?? ""}
              onSend={handleSend}
              onMarkRead={handleMarkRead}
              isLoading={msgsLoading}
              conversationMembers={projectConvo?.members}
            />
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8">
              <MessageSquare className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm mb-3">No project chat yet. Send a message to start.</p>
              <MessageThread
                messages={[]}
                currentUserId={user?.id ?? ""}
                onSend={handleSend}
                onMarkRead={handleMarkRead}
                conversationMembers={[]}
              />
            </div>
          )}
        </div>
      </TabsContent>

      <TabsContent value="tasks">
        <ProjectTasklist projectId={projectId} />
      </TabsContent>
    </Tabs>
  );
};
